import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  User,
  Lock,
  Bell,
  Globe,
  Pencil,
  Eye,
  EyeOff,
  Settings as SettingsIcon,
} from 'lucide-react';
import { useDataService, useStorageService } from '@/lib/service-context';
import { useProAuth } from '@/lib/auth-context';
import { useCart } from '@/lib/cart-context';
import { useProPrefs, writeProPrefs } from '@/lib/user-prefs';
import {
  isProPasswordValid,
  PRO_MIN_PASSWORD_LENGTH,
  type RemiseTier,
} from '@erp/domain/pro';
import { EmailPreferences } from '@/components/settings/EmailPreferences';
import { TabsBar } from '@/components/common/TabsBar';

type Tab = 'profile' | 'security' | 'notifications' | 'language' | 'options';

// Static keys so the i18n audit tooling sees them (no template-built keys).
const TIER_OPTIONS: { value: RemiseTier; labelKey: string; label: string }[] = [
  { value: 'min', labelKey: 'pro.cart.tier_min', label: 'Min' },
  { value: 'moy', labelKey: 'pro.settings.tier_moyenne', label: 'Moyenne' },
  { value: 'max', labelKey: 'pro.cart.tier_max', label: 'Max' },
];

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const ds = useDataService();
  const qc = useQueryClient();
  const storage = useStorageService();
  const { proCustomer, refreshProCustomer, updatePassword } = useProAuth();
  const [tab, setTab] = useState<Tab>('profile');
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [photoErr, setPhotoErr] = useState<string | null>(null);

  const uploadPhoto = useMutation({
    mutationFn: async (file: File) => {
      setPhotoErr(null);
      if (!proCustomer) throw new Error('Profile non chargé');
      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const path = `${proCustomer.id}/avatar-${Date.now()}.${ext}`;
      await storage.upload('pro-avatars', path, file, { contentType: file.type });
      const url = storage.getPublicUrl('pro-avatars', path);
      await ds.proCustomers.updateSelf({ profilePictureUrl: url });
    },
    onSuccess: async () => {
      await refreshProCustomer();
      qc.invalidateQueries({ queryKey: ['pro-customer'] });
    },
    onError: (err) => {
      setPhotoErr(err instanceof Error ? err.message : String(err));
    },
  });
  const [firstName, setFirstName] = useState(proCustomer?.firstName ?? '');
  const [lastName, setLastName] = useState(proCustomer?.lastName ?? '');
  const [phone, setPhone] = useState(proCustomer?.phone ?? '');
  const [whatsapp, setWhatsapp] = useState(proCustomer?.whatsapp ?? '');

  // Préférences locales partagées (notifications, affichage commission) —
  // même store que le panier, donc un toggle ici se répercute immédiatement.
  const prefs = useProPrefs();

  // Base de prix par défaut — persistée côté serveur (pro_customers), le pro
  // peut la changer lui-même. Le panier courant adopte le nouveau palier tout
  // de suite (adoptDefaultTier efface le choix explicite du panier).
  const { adoptDefaultTier } = useCart();
  const [tierSaved, setTierSaved] = useState(false);
  const saveTier = useMutation({
    mutationFn: (tier: RemiseTier) =>
      ds.proCustomers.updateSelf({ defaultRemiseTier: tier }),
    onSuccess: async (updated) => {
      adoptDefaultTier(updated.defaultRemiseTier);
      await refreshProCustomer();
      setTierSaved(true);
    },
  });

  // Password change form
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState(false);

  const passwordSave = useMutation({
    mutationFn: async () => {
      setPwError(null);
      setPwSuccess(false);
      // Longueur seule : aucune règle de composition dans l'espace électricien.
      if (!isProPasswordValid(newPassword)) {
        throw new Error(
          t(
            'pro.settings.pw_too_short',
            'Le mot de passe doit faire au moins {{min}} caractères',
            { min: PRO_MIN_PASSWORD_LENGTH },
          ),
        );
      }
      if (newPassword !== confirmPassword) {
        throw new Error(
          t('pro.settings.pw_mismatch', 'Les mots de passe ne correspondent pas'),
        );
      }
      await updatePassword(newPassword);
    },
    onSuccess: () => {
      setPwSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (err) => {
      setPwError(err instanceof Error ? err.message : String(err));
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      await ds.proCustomers.updateSelf({
        firstName,
        lastName,
        phone,
        whatsapp,
      });
    },
    onSuccess: async () => {
      await refreshProCustomer();
      qc.invalidateQueries({ queryKey: ['pro-customer'] });
    },
  });

  const initials = (() => {
    const f = (proCustomer?.firstName ?? '').charAt(0).toUpperCase();
    const l = (proCustomer?.lastName ?? '').charAt(0).toUpperCase();
    return (f + l) || 'TP';
  })();

  const tabs: { key: Tab; label: string; icon: typeof User }[] = [
    { key: 'profile', label: t('pro.settings.tab_profile', 'Profil'), icon: User },
    { key: 'security', label: t('pro.settings.tab_security', 'Sécurité'), icon: Lock },
    { key: 'notifications', label: t('pro.settings.tab_notifications', 'Notification'), icon: Bell },
    { key: 'language', label: t('pro.settings.tab_language', 'Langue'), icon: Globe },
    { key: 'options', label: t('pro.settings.tab_options', 'Options'), icon: SettingsIcon },
  ];

  return (
    <div className="pro-card p-0 overflow-hidden lg:grid lg:grid-cols-[260px_1fr] min-h-[480px]">
      {/* Mobile : bande d'onglets défilante. La colonne de gauche empilée
          mangeait tout l'écran avant d'arriver au contenu. */}
      <div
        className="lg:hidden px-2"
        style={{ borderBottom: '1px solid var(--pro-line)' }}
      >
        <TabsBar
          variant="underline"
          tabs={tabs.map((tb) => {
            const Icon = tb.icon;
            return {
              key: tb.key,
              label: (
                <span className="inline-flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  {tb.label}
                </span>
              ),
            };
          })}
          value={tab}
          onChange={setTab}
        />
      </div>

      {/* Desktop : colonne latérale */}
      <aside
        className="hidden lg:block p-4 space-y-1"
        style={{
          borderRight: '1px solid var(--pro-line)',
        }}
      >
        {tabs.map((tb) => {
          const Icon = tb.icon;
          const active = tab === tb.key;
          return (
            <button
              key={tb.key}
              type="button"
              onClick={() => setTab(tb.key)}
              className="w-full flex items-center gap-2.5 px-3 h-11 rounded-lg text-[14px] font-medium"
              style={
                active
                  ? {
                      background: 'var(--pro-primary-subtle)',
                      color: 'var(--pro-primary)',
                      fontFamily: 'Figtree',
                    }
                  : { color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }
              }
            >
              <Icon className="w-[18px] h-[18px]" />
              {tb.label}
            </button>
          );
        })}
      </aside>

      {/* Content */}
      <div key={tab} className="p-4 sm:p-6 space-y-6 pro-fade-in">
        {tab === 'profile' && (
          <>
            <h2
              className="text-[18px] font-bold"
              style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
            >
              {t('pro.settings.profile_title', 'Profil')}
            </h2>
            <div>
              <p
                className="text-[14px] font-semibold mb-3"
                style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
              >
                {t('pro.settings.profile_photo', 'Photo de profil')}
              </p>
              <div className="relative inline-block">
                <div
                  className="w-[84px] h-[84px] rounded-full flex items-center justify-center text-[28px] font-bold overflow-hidden"
                  style={{
                    background: 'var(--pro-primary-subtle)',
                    color: 'var(--pro-primary)',
                    fontFamily: 'Figtree',
                  }}
                >
                  {proCustomer?.profilePictureUrl ? (
                    <img
                      src={proCustomer.profilePictureUrl}
                      alt={initials}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    initials
                  )}
                </div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="sr-only"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadPhoto.mutate(f);
                    e.target.value = ''; // allow re-selecting same file
                  }}
                />
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadPhoto.isPending}
                  className="absolute bottom-0 right-0 w-7 h-7 rounded-full flex items-center justify-center text-white disabled:opacity-60"
                  style={{ background: 'var(--pro-primary)', border: '2px solid white' }}
                  aria-label={t('pro.settings.edit_photo', 'Modifier la photo')}
                >
                  {uploadPhoto.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Pencil className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
              {photoErr && (
                <p className="text-[12px] mt-2" style={{ color: 'var(--pro-danger)' }}>
                  {photoErr}
                </p>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate();
              }}
              className="space-y-4"
            >
              <p
                className="text-[14px] font-semibold"
                style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
              >
                {t('pro.settings.profile_info', 'Informations personnelles')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label={t('pro.settings.first_name', 'Prénom')}
                  value={firstName}
                  onChange={setFirstName}
                  required
                />
                <Field
                  label={t('pro.settings.last_name', 'Nom')}
                  value={lastName}
                  onChange={setLastName}
                  required
                />
              </div>
              <Field
                label={t('pro.settings.phone', 'Numéro de téléphone')}
                value={phone ?? ''}
                onChange={setPhone}
                prefix="+216"
                required
                inputMode="tel"
              />
              <Field
                label={t('pro.settings.whatsapp', 'WhatsApp')}
                value={whatsapp ?? ''}
                onChange={setWhatsapp}
                prefix="+216"
                inputMode="tel"
              />
              <Field
                label={t('pro.settings.email', 'Adresse e-mail')}
                value={proCustomer?.email ?? ''}
                onChange={() => {}}
                readonly
              />

              <button
                type="submit"
                disabled={save.isPending}
                className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-lg text-[14px] font-semibold text-white disabled:opacity-60"
                style={{ background: 'var(--pro-primary)', fontFamily: 'Figtree' }}
              >
                {save.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('pro.settings.save', 'Enregistrer')}
              </button>
            </form>
          </>
        )}

        {tab === 'security' && (
          <>
            <h2
              className="text-[18px] font-bold"
              style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
            >
              {t('pro.settings.security_title', 'Sécurité')}
            </h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                passwordSave.mutate();
              }}
              className="space-y-4 max-w-md"
            >
              <p
                className="text-[14px] font-semibold"
                style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
              >
                {t('pro.settings.change_password', 'Changer mon mot de passe')}
              </p>
              <PasswordField
                label={t('pro.settings.new_password', 'Nouveau mot de passe')}
                value={newPassword}
                onChange={setNewPassword}
                visible={showNew}
                onToggleVisible={() => setShowNew((v) => !v)}
                hint={t('pro.settings.pw_hint', '{{min}} caractères minimum', {
                  min: PRO_MIN_PASSWORD_LENGTH,
                })}
              />
              <PasswordField
                label={t('pro.settings.confirm_password', 'Confirmer le mot de passe')}
                value={confirmPassword}
                onChange={setConfirmPassword}
                visible={showConfirm}
                onToggleVisible={() => setShowConfirm((v) => !v)}
              />
              {pwError && (
                <div
                  className="p-3 rounded-lg text-[13px]"
                  style={{
                    background: 'rgba(223,28,65,0.08)',
                    color: 'var(--pro-danger)',
                    border: '1px solid rgba(223,28,65,0.2)',
                  }}
                >
                  {pwError}
                </div>
              )}
              {pwSuccess && (
                <div
                  className="p-3 rounded-lg text-[13px]"
                  style={{
                    background: 'rgba(29,175,97,0.08)',
                    color: 'var(--pro-success)',
                    border: '1px solid rgba(29,175,97,0.2)',
                  }}
                >
                  {t('pro.settings.pw_updated', 'Mot de passe mis à jour')}
                </div>
              )}
              <button
                type="submit"
                disabled={passwordSave.isPending || !newPassword || !confirmPassword}
                className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-lg text-[14px] font-semibold text-white disabled:opacity-60"
                style={{ background: 'var(--pro-primary)', fontFamily: 'Figtree' }}
              >
                {passwordSave.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('pro.settings.update_password', 'Mettre à jour')}
              </button>
            </form>
          </>
        )}

        {tab === 'notifications' && (
          <>
            <h2
              className="text-[18px] font-bold"
              style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
            >
              {t('pro.settings.notif_title', 'Notification')}
            </h2>
            <div className="space-y-1 max-w-md">
              <NotifRow
                label={t('pro.settings.notif_general', 'Notification générale')}
                description={t(
                  'pro.settings.notif_general_desc',
                  "Recevoir les mises à jour de l'application",
                )}
                checked={prefs.notifGeneral}
                onChange={(v) => writeProPrefs({ notifGeneral: v })}
              />
              <NotifRow
                label={t('pro.settings.notif_email', 'Email')}
                description={t(
                  'pro.settings.notif_email_desc',
                  'Recevoir les confirmations par email',
                )}
                checked={prefs.notifEmail}
                onChange={(v) => writeProPrefs({ notifEmail: v })}
              />
              <NotifRow
                label={t('pro.settings.notif_whatsapp', 'WhatsApp')}
                description={t(
                  'pro.settings.notif_whatsapp_desc',
                  'Recevoir les confirmations par WhatsApp',
                )}
                checked={prefs.notifWhatsapp}
                onChange={(v) => writeProPrefs({ notifWhatsapp: v })}
              />
            </div>
          </>
        )}

        {tab === 'language' && (
          <>
            <h2
              className="text-[18px] font-bold"
              style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
            >
              {t('pro.settings.lang_title', 'Langue')}
            </h2>
            <div className="space-y-1">
              {[
                { v: 'fr', l: 'Français' },
                { v: 'ar', l: 'العربية' },
                { v: 'en', l: 'English (États-Unis)' },
              ].map((opt) => (
                <label
                  key={opt.v}
                  className="flex items-center justify-between py-3 cursor-pointer"
                  style={{ borderBottom: '1px solid var(--pro-line)' }}
                >
                  <span style={{ color: 'var(--pro-text-dark)' }}>{opt.l}</span>
                  <input
                    type="radio"
                    name="lang"
                    checked={i18n.language === opt.v}
                    onChange={() => i18n.changeLanguage(opt.v)}
                    className="w-5 h-5 accent-orange-500"
                  />
                </label>
              ))}
            </div>
          </>
        )}

        {tab === 'options' && (
          <>
            <h2
              className="text-[18px] font-bold"
              style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
            >
              {t('pro.settings.options_title', 'Options')}
            </h2>
            <div className="space-y-6 max-w-md">
              {/* Base de prix par défaut — le pro choisit son palier. */}
              <div>
                <label
                  className="text-[14px] font-medium mb-1.5 block"
                  style={{ color: 'var(--pro-text-muted)' }}
                >
                  {t('pro.settings.default_tier', 'Base de prix par défaut')}
                </label>
                <p
                  className="text-[12px] mb-3"
                  style={{ color: 'var(--pro-text-grey)' }}
                >
                  {t(
                    'pro.settings.default_tier_desc',
                    'Palier de remise appliqué par défaut à votre panier (Min, Moyenne ou Max). Vous pouvez toujours en changer ponctuellement depuis le panier.',
                  )}
                </p>
                <div
                  className="inline-flex items-center gap-1"
                  role="group"
                  aria-label={t(
                    'pro.settings.default_tier',
                    'Base de prix par défaut',
                  )}
                >
                  {TIER_OPTIONS.map((opt) => {
                    const active =
                      opt.value === (proCustomer?.defaultRemiseTier ?? 'moy');
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        aria-pressed={active}
                        disabled={saveTier.isPending}
                        onClick={() => {
                          if (active) return;
                          setTierSaved(false);
                          saveTier.mutate(opt.value);
                        }}
                        className={`inline-flex items-center justify-center h-9 px-4 rounded-lg text-[13px] font-semibold select-none transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                          active ? '' : 'hover:bg-neutral-100'
                        }`}
                        style={
                          active
                            ? {
                                background: 'var(--pro-primary-subtle)',
                                color: 'var(--pro-primary-text)',
                                border: '1px solid var(--pro-primary)',
                              }
                            : {
                                color: 'var(--pro-text-muted)',
                                border: '1px solid var(--pro-border)',
                              }
                        }
                      >
                        {t(opt.labelKey, opt.label)}
                      </button>
                    );
                  })}
                  {saveTier.isPending && (
                    <Loader2
                      className="w-4 h-4 ml-2 animate-spin"
                      style={{ color: 'var(--pro-primary)' }}
                    />
                  )}
                </div>
                {tierSaved && !saveTier.isPending && (
                  <p
                    className="text-[12px] mt-2"
                    style={{ color: 'var(--pro-success)' }}
                  >
                    {t(
                      'pro.settings.tier_saved',
                      'Base de prix enregistrée — votre panier utilise ce palier.',
                    )}
                  </p>
                )}
                {saveTier.isError && (
                  <p
                    className="text-[12px] mt-2"
                    style={{ color: 'var(--pro-danger)' }}
                  >
                    {t(
                      'pro.settings.tier_save_error',
                      "Impossible d'enregistrer la base de prix. Réessayez.",
                    )}
                  </p>
                )}
              </div>

              {/* Commission — seule la VISIBILITÉ est exposée au pro : le taux
                  exact fixé par l'administrateur ne doit pas être affiché
                  (demande Khalil 2026-07-05), uniquement les montants estimés
                  sur le panier / la validation. */}
              <div
                className="pt-5 space-y-4"
                style={{ borderTop: '1px solid var(--pro-line)' }}
              >
                <NotifRow
                  label={t(
                    'pro.settings.show_commission',
                    'Afficher ma commission',
                  )}
                  description={t(
                    'pro.settings.show_commission_desc',
                    'Montrer la commission estimée dans le panier et à la validation de commande.',
                  )}
                  checked={prefs.showCommission}
                  onChange={(v) => writeProPrefs({ showCommission: v })}
                />
              </div>

              {/* Emails — même colonne que le lien « se désabonner » du pied
                  des emails, pas une préférence parallèle. */}
              <div
                className="pt-5"
                style={{ borderTop: '1px solid var(--pro-line)' }}
              >
                <EmailPreferences proCustomer={proCustomer ?? null} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PasswordField({
  label,
  value,
  onChange,
  visible,
  onToggleVisible,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  visible: boolean;
  onToggleVisible: () => void;
  hint?: string;
}) {
  return (
    <div>
      <label
        className="text-[14px] font-medium mb-1.5 block"
        style={{ color: 'var(--pro-text-muted)' }}
      >
        {label}
      </label>
      <div
        className="flex items-stretch h-11 rounded-lg overflow-hidden"
        style={{ border: '1px solid var(--pro-border)', background: 'white' }}
      >
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 h-11 text-[14px] focus:outline-none bg-transparent"
          style={{ color: 'var(--pro-text-dark)' }}
          autoComplete="new-password"
        />
        <button
          type="button"
          onClick={onToggleVisible}
          className="px-3 inline-flex items-center justify-center"
          style={{ color: 'var(--pro-text-grey)' }}
          aria-label={visible ? 'Cacher' : 'Afficher'}
        >
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {hint && (
        <p className="text-[12px] mt-1" style={{ color: 'var(--pro-text-grey)' }}>
          {hint}
        </p>
      )}
    </div>
  );
}

function NotifRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div
      className="flex items-center justify-between py-3 gap-4"
      style={{ borderBottom: '1px solid var(--pro-line)' }}
    >
      <div className="flex-1 min-w-0">
        <div
          className="text-[14px] font-medium"
          style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
        >
          {label}
        </div>
        <div
          className="text-[12px]"
          style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
        >
          {description}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className="w-10 h-6 rounded-full relative shrink-0"
        style={{
          background: checked ? 'var(--pro-primary)' : 'var(--pro-border)',
        }}
        aria-pressed={checked}
      >
        <span
          className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow"
          style={{
            left: checked ? 'calc(100% - 22px)' : '2px',
            transition: 'left 280ms cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        />
      </button>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  prefix,
  readonly,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  prefix?: string;
  readonly?: boolean;
  inputMode?: 'tel' | 'numeric';
}) {
  return (
    <div>
      <label
        className="text-[14px] font-medium mb-1.5 block"
        style={{ color: 'var(--pro-text-muted)' }}
      >
        {label} {required && <span style={{ color: 'var(--pro-danger)' }}>*</span>}
      </label>
      <div
        className="flex items-stretch h-13 rounded-xl overflow-hidden"
        style={{
          border: '1px solid var(--pro-border)',
          background: readonly ? 'var(--pro-grey-subtle)' : 'white',
        }}
      >
        {prefix && (
          <div
            className="flex items-center px-3 text-[14px] font-medium"
            style={{
              background: 'var(--pro-grey-subtle)',
              color: 'var(--pro-text-dark)',
              borderRight: '1px solid var(--pro-line)',
            }}
          >
            🇹🇳 {prefix}
          </div>
        )}
        <input
          type="text"
          required={required}
          readOnly={readonly}
          inputMode={inputMode}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 h-13 text-[14px] focus:outline-none"
          style={{ color: 'var(--pro-text-dark)', background: 'transparent' }}
          placeholder={readonly ? '' : `Taper votre ${label.toLowerCase()}`}
        />
      </div>
    </div>
  );
}
